import React, { useState } from 'react';
import Checkbox from '@common/checkbox';
import { usePHSPACCResultsFeedback } from '@components/phs/pacc/results/query/useFeedback';
import { useUserInfo } from '@repository/auth/useUserInfo';
import RatingStars from '../stars';
import { FeedbackStep } from '../index';
import classNames from 'classnames/bind';
import styles from './feedbackSurvey.module.scss';

const cx = classNames.bind(styles);

const optionsContent = [
  '역량유형 콘텐츠 - 나의 역량유형과 특징 및 꿀팁을 알려줘서 좋았어요.',
  '잘 맞는 기업문화 및 기업 추천 - 내게 맞는 기업문화와 기업을 명확하게 알려줘서 좋았어요.',
  '역량 프로파일 및 강/약점 - 내가 가진 역량 프로파일과 강점, 약점을 자세하게 알려줘서 좋았어요.',
  '나에게 잘 맞는 직군/직무 추천 - 나에게 잘 맞는 직군/직무를 확인할 수 있어서 좋았어요.',
  '강/약점을 보완할 면접 가이드 - 내 강점과 약점에 따라 면접 예상질문과 가이드를 줘서 좋았어요.',
];

interface FeedBackSurveyProps {
  sn: number;
  step: FeedbackStep;
  setStep: (step: FeedbackStep) => void;
  nextStep: () => void;
}

const FeedBackSurvey = ({ sn, step, setStep, nextStep }: FeedBackSurveyProps) => {
  const [ratingValue, setRatingValue] = useState(0);
  const [options, setOptions] = useState<boolean[]>([false, false, false, false, false]);
  const { data: userInfo } = useUserInfo();
  const userName = userInfo?.name ?? '';

  const isOptionDisabled = (val: number) => !options[val] && options.filter((v) => v).length > 2;

  const isSubmitDisabled = options.every((v) => !v);

  const handleChangeCheck = (idx: number, checked: boolean) => {
    const arr = [...options];
    arr[idx] = checked;
    setOptions(arr);
  };

  const handleClickStar = (idx: number) => {
    setRatingValue(idx);
    if (step === FeedbackStep.INIT) nextStep();
  };

  const handleMouseOverStar = (idx: number) => {
    if (step > 0) return;
    setRatingValue(idx);
  };

  const getFavorites = () => {
    return options.reduce<string[]>((acc, val, idx) => {
      if (val) acc.push(optionsContent[idx]);
      return acc;
    }, []);
  };

  const { mutate: fetchFeedback } = usePHSPACCResultsFeedback(
    { favorites: getFavorites(), rating: ratingValue, sn },
    () => setStep(FeedbackStep.SUBMIT_TRANSITION),
  );

  const handleClickSubmit = async () => {
    if (step !== FeedbackStep.SHOW_OPTIONS) return;
    fetchFeedback();
  };

  const optionList = optionsContent.map((v, i) => (
    <li key={i} className={cx('listItem')}>
      <Checkbox
        onChange={(e) => handleChangeCheck(i, e.currentTarget.checked)}
        checked={options[i]}
        disabled={isOptionDisabled(i)}
        small
      >
        <span className={cx('optionText')}>{v}</span>
      </Checkbox>
    </li>
  ));

  return (
    <>
      <div className={cx('ratingArea')}>
        <em className={cx('title')}>{userName}님의 역량 이해에 얼마나 도움이 되었나요?</em>
        <div className={cx('ratingStarArea')}>
          <RatingStars
            onClick={handleClickStar}
            onMouseOver={handleMouseOverStar}
            clicked={step !== FeedbackStep.INIT}
            value={ratingValue}
          />
        </div>
      </div>
      <div className={cx('surveyArea')}>
        {step < FeedbackStep.SHOW_OPTIONS && (
          <div onTransitionEnd={nextStep} className={cx('joy')} style={{ opacity: step ? 0 : 1 }} />
        )}
        {step > FeedbackStep.CLICK_STAR_TRANSITION && (
          <div className={cx('surveyOptionArea')}>
            <div onTransitionEnd={(e) => e.stopPropagation()} className={cx('formWrapper')}>
              <em className={cx('title')}>특히 좋았던 점을 체크해주세요. (최대 3개 선택 가능)</em>
              <ul className={cx('optionList')}>{optionList}</ul>

              <div className={cx('buttonWrapper')}>
                <button className={cx('submitButton')} disabled={isSubmitDisabled} onClick={handleClickSubmit}>
                  피드백 제출하기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FeedBackSurvey;
